import * as React from 'react';

import type { ProfileOccupationChoices_OccupationChoice as LearnerProfileOccupationChoice } from '__generated__/graphql-types';

import { StandaloneCheckbox, Typography, Typography2 } from '@coursera/cds-core';
import type { ViewForm as EventDataViewForm } from '@coursera/event-pulse-types';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { convertProfileOccupationChoiceToPreferenceNaptimeType } from 'bundles/employability-common/queries/utils/convertProfileOccupationChoiceToPreferenceNaptimeType';
import type { AutoCompleteInput } from 'bundles/enterprise-learner-onboarding/components/LearnerOnboardingContainer';
import { Spacer } from 'bundles/enterprise-ui';
import { AutoCompleteableInput } from 'bundles/onboarding-2022/components/AutoCompleteableInput';
import type { OnChangeEvent } from 'bundles/onboarding-2022/types/OnChangeInput';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/program-home';

const TrackedCheckbox = withSingleTracked({ type: 'CHECKBOX' })(StandaloneCheckbox);

export type PropsFromCaller = {
  roleInputText: string;
  allOptions: Array<LearnerProfileOccupationChoice>;
  onRoleInputTextChange: (input: AutoCompleteInput) => void;
  isUnsureRole: boolean;
  onUnsureRoleChange: () => void;
  showUnsureOption: boolean;
  label: string;
  sharedEventingV3Data: Omit<EventDataViewForm, 'page' | 'user_properties'>;
};

export const RoleInputStep = ({
  allOptions,
  isUnsureRole,
  onUnsureRoleChange,
  onRoleInputTextChange,
  roleInputText,
  showUnsureOption,
  label,
  sharedEventingV3Data,
}: PropsFromCaller) => {
  const allOptionsProfileOccupationPreference = convertProfileOccupationChoiceToPreferenceNaptimeType(allOptions);
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_form', {
    ...sharedEventingV3Data,
    form: { ...sharedEventingV3Data.form, fieldsAvailable: showUnsureOption ? 2 : 1 },
  });

  return (
    <TrackedDiv
      trackingName="role_input_step"
      data-testid="role-input-step"
      requireFullyVisible={false}
      withVisibilityTracking
      trackClicks={false}
    >
      {/* Additional div as a workaround to get visibility tracking to work for eventing v3 */}
      <div ref={visibilityTrackingRef}>
        <Typography component="h1" variant="h1semibold">
          {label}
        </Typography>
        <Spacer size={12} />
        <Typography2 component="p" variant="bodyPrimary">
          {_t('Enter your role below. It will help us recommend courses to help you achieve your goals.')}
        </Typography2>
        <Spacer size={24} />
        <AutoCompleteableInput
          question={_t('Role')}
          questionId="role-input"
          onChange={onRoleInputTextChange as (e: OnChangeEvent) => void}
          defaultValue={{ id: '', name: roleInputText ?? '' }}
          allOptions={allOptionsProfileOccupationPreference}
          disabled={Boolean(isUnsureRole && showUnsureOption)}
        />
        {showUnsureOption && (
          <>
            <Spacer size={24} />
            <TrackedCheckbox
              trackingName="unsure_role"
              trackingData={{ value: !isUnsureRole }}
              aria-label={_t('I am not sure of my role. Show me everything.')}
              checked={isUnsureRole}
              label={_t("I'm not sure. Show me everything.")}
              value="unsure-role"
              onChange={onUnsureRoleChange}
            />
          </>
        )}
        <Spacer size={48} />
      </div>
    </TrackedDiv>
  );
};
